import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Expense } from '../types';
import { formatCurrency } from '../utils/formatters';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  expense: Expense | null;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  expense,
  onConfirm,
  onCancel,
  loading = false
}) => {
  if (!isOpen || !expense) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal delete-confirmation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="delete-modal-header-content">
            <div className="delete-icon-container">
              <AlertTriangle size={24} className="delete-warning-icon" />
            </div>
            <h2 className="heading-2">Delete Expense</h2>
          </div>
          <button
            onClick={onCancel}
            className="btn btn-icon btn-secondary"
            disabled={loading}
          >
            <X size={16} />
          </button>
        </div>

        <div className="modal-body">
          <div className="delete-confirmation-content">
            <p className="delete-confirmation-text">
              Are you sure you want to delete this expense? This action cannot be undone.
            </p>

            <div className="expense-preview">
              <div className="expense-preview-header">
                <h4 className="expense-preview-title">Expense Details:</h4>
              </div>
              <div className="expense-preview-details">
                <div className="expense-detail-row">
                  <span className="expense-detail-label">Name:</span>
                  <span className="expense-detail-value">{expense.name}</span>
                </div>
                <div className="expense-detail-row">
                  <span className="expense-detail-label">Amount:</span>
                  <span className="expense-detail-value expense-amount">
                    {formatCurrency(expense.amount, expense.currency)}
                  </span>
                </div>
                <div className="expense-detail-row">
                  <span className="expense-detail-label">Category:</span>
                  <span className="expense-detail-value">{expense.category?.name || 'Uncategorized'}</span>
                </div>
                {expense.description && (
                  <div className="expense-detail-row">
                    <span className="expense-detail-label">Description:</span>
                    <span className="expense-detail-value">{expense.description}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="btn btn-danger"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner" />
                Deleting...
              </>
            ) : (
              <>
                <AlertTriangle size={16} />
                Delete Expense
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
